/**
 * computeClavicleSupportFields — the geodesic ruler both ADR-021 stages share:
 * for each clavicle, the through-volume distance from its DISTAL end (the
 * acromion — where the shoulder cap actually is) to every skinned vertex. The
 * Stage-A gate reads it as "how far does this bone's weight reach"; the Stage-B
 * repair reads it as "is this vertex inside the bone's legitimate support".
 * ONE module so the two stages can never disagree about the metric.
 *
 * WHY THE ACROMION POINT AND NOT THE SEGMENT. The clavicle JOINT sits at the
 * sternum end — exactly where the measured defect lives (28% clavicle ownership
 * AT the midline). A field seeded from the segment (or any distal sub-span)
 * calls the medial chest "close" and the bound can never cull it — the first
 * law-test run measured exactly that: sub-segment seeds plus the seed radius
 * pushed the effective origin ~0.5 clavicle lengths medial and the repair
 * no-opped. Anatomy says clavicle weight belongs to the SHOULDER-CAP region
 * (ADR-021 §2 Stage B), so the field is rooted at the cap's own landmark: the
 * ARM joint — the acromion, the clavicle's distal seat. Distance grows
 * medially across the chest from there: the sternum sits ~1.2 clavicle lengths
 * away (measured, default male), the deltoid cap within ~0.5.
 *
 * WHY GEODESIC (Dionne & de Lasa 2013, via the vendored Voxel-Skinner lineage):
 * Euclidean distance tunnels through air — a raised arm's elbow can be
 * Euclidean-near the ribs. Through-volume BFS distance (hands/bind-weights
 * `geodesicField`, the exact machinery the hand bind uses) is the metric under
 * which "far from the shoulder" means what a deformation cares about.
 *
 * Fail-loud: a grid without a world embedding throws (wiring bug). A distal
 * seed that misses the volume snaps to the nearest occupied voxel and is
 * REPORTED (`clavicle-seed-snapped` marker + `seedSnapDistanceVoxels`) — the
 * caller decides how far is too far (the repair refuses past its threshold,
 * ADR-021 §7). Vertices whose stencil finds no occupied voxel get Infinity and
 * a counted marker, never a silent zero.
 *
 * Depends on: hands/voxel-grid (VolumeGrid), hands/bind-weights (geodesicField),
 * weights/skin-data, weights/chest-bones, math/vec3. Depended on by:
 * weights/chest-integrity, weights/bound-influence, kernel index.
 */
import { geodesicField } from '../hands/bind-weights.js';
import type { EvidenceMarker } from '../hands/separation-profile.js';
import { NoWorldEmbeddingError, voxelCoords, voxelIndex, type VolumeGrid } from '../hands/voxel-grid.js';
import { vec3Length, vec3Sub } from '../math/vec3.js';
import type { ChestBones, ResolvedChestBone } from './chest-bones.js';
import { skinVertexCount, validateSkinData, type SkinVertexData } from './skin-data.js';

/**
 * The acromion seed is a BALL of 0.2 clavicle lengths (never under 1.5
 * voxels): generated rigs seat joints off the flesh centroid (Tripo ~2.25cm
 * anterior, [[tripo-anterior-joint-offset]]), and a point seed biases every
 * distance against the far side of the deltoid — measured as cap-follow
 * degradation on the default male. The ball covers the offset.
 */
const SEED_RADIUS_FRACTION = 0.2;
const SEED_RADIUS_MIN_VOXELS = 1.5;

/** One clavicle's support field: per-vertex geodesic distance from its distal seat. */
export interface ClavicleSupportField {
  readonly side: 'left' | 'right';
  readonly clavicle: ResolvedChestBone;
  /** World-units length of the clavicle segment (clavicle joint → arm joint). */
  readonly clavicleLength: number;
  /**
   * Per-vertex geodesic distance from the distal clavicle seed, in WORLD units.
   * `Infinity` where the vertex's voxel stencil found no occupied volume or the
   * volume is unreachable from the seed.
   */
  readonly distances: Float32Array;
  /** How far the distal seed had to snap to reach occupied volume (0 = seated). */
  readonly seedSnapDistanceVoxels: number;
}

export interface SupportFieldsResult {
  readonly left: ClavicleSupportField;
  readonly right: ClavicleSupportField;
  readonly markers: readonly EvidenceMarker[];
}

/**
 * Compute both clavicles' support fields over one shared voxelization. See the
 * module header for the full contract.
 */
export function computeClavicleSupportFields(
  grid: VolumeGrid,
  skin: SkinVertexData,
  bones: ChestBones,
): SupportFieldsResult {
  if (grid.origin === undefined) throw new NoWorldEmbeddingError();
  validateSkinData(skin);

  const dims = grid.dims;
  const origin = grid.origin;
  const h = grid.voxelSize;
  const occupiedList = [...grid.occupied].sort((a, b) => a - b);
  const slot = new Int32Array(dims[0] * dims[1] * dims[2]).fill(-1);
  occupiedList.forEach((v, i) => {
    slot[v] = i;
  });

  const markers: EvidenceMarker[] = [];
  const n = skinVertexCount(skin);
  let outsideVolume = 0;

  const sideField = (
    side: 'left' | 'right',
    clavicle: ResolvedChestBone,
    arm: ResolvedChestBone,
  ): ClavicleSupportField => {
    const clavicleLength = vec3Length(vec3Sub(arm.worldPosition, clavicle.worldPosition));
    // The acromion in voxel-index space (voxel-CENTER convention).
    const seat: readonly [number, number, number] = [
      (arm.worldPosition.x - origin[0]!) / h - 0.5,
      (arm.worldPosition.y - origin[1]!) / h - 0.5,
      (arm.worldPosition.z - origin[2]!) / h - 0.5,
    ];

    const seedRadius = Math.max(SEED_RADIUS_MIN_VOXELS, (SEED_RADIUS_FRACTION * clavicleLength) / h);
    const r2 = seedRadius * seedRadius;
    const seeds: number[] = [];
    let nearest = -1;
    let nearestD2 = Infinity;
    for (const v of occupiedList) {
      const c = voxelCoords(v, dims);
      const dx = c[0] - seat[0];
      const dy = c[1] - seat[1];
      const dz = c[2] - seat[2];
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 <= r2) seeds.push(v);
      if (d2 < nearestD2) {
        nearestD2 = d2;
        nearest = v;
      }
    }
    let seedSnapDistanceVoxels = 0;
    if (seeds.length === 0) {
      // The distal clavicle sits off the voxelized flesh (thin strap, coarse
      // grid, or a joint authored outside the mesh — Tripo seats joints ~2cm
      // anterior). Snap to the nearest occupied voxel and SAY SO; the caller
      // owns the too-far refusal.
      if (nearest >= 0) seeds.push(nearest);
      seedSnapDistanceVoxels = Math.sqrt(nearestD2);
      markers.push({
        code: 'clavicle-seed-snapped',
        detail: { side, bone: clavicle.rigBone, snapDistanceVoxels: Number(seedSnapDistanceVoxels.toFixed(3)) },
      });
    }

    const field = geodesicField(seeds, dims, slot, occupiedList);
    const distances = new Float32Array(n).fill(Infinity);
    for (let v = 0; v < n; v += 1) {
      const px = (skin.positions[v * 3]! - origin[0]!) / h - 0.5;
      const py = (skin.positions[v * 3 + 1]! - origin[1]!) / h - 0.5;
      const pz = (skin.positions[v * 3 + 2]! - origin[2]!) / h - 0.5;
      // Min over the occupied corners of the vertex's 8-voxel stencil: for a
      // BOUND ruler the conservative (nearest-corner) read is the right bias —
      // half-voxel quantization is absorbed by the repair's feather.
      const x0 = Math.floor(px);
      const y0 = Math.floor(py);
      const z0 = Math.floor(pz);
      let best = Infinity;
      for (let c = 0; c < 8; c += 1) {
        const ix = x0 + (c & 1);
        const iy = y0 + ((c >> 1) & 1);
        const iz = z0 + ((c >> 2) & 1);
        if (ix < 0 || iy < 0 || iz < 0 || ix >= dims[0] || iy >= dims[1] || iz >= dims[2]) continue;
        const s = slot[voxelIndex(ix, iy, iz, dims)]!;
        if (s < 0) continue;
        const d = field[s]!;
        if (d < best) best = d;
      }
      if (best === Infinity) outsideVolume += 1;
      else distances[v] = best * h;
    }
    return { side, clavicle, clavicleLength, distances, seedSnapDistanceVoxels };
  };

  const left = sideField('left', bones.clavicleL, bones.armL);
  const right = sideField('right', bones.clavicleR, bones.armR);
  if (outsideVolume > 0) {
    // Counted across both sides: a vertex off the volume is off it for both.
    markers.push({ code: 'support-vertices-outside-volume', detail: { count: outsideVolume / 2 } });
  }
  return { left, right, markers };
}

